import { useCallback, useEffect, useMemo, useState, type FormEvent, type ReactElement } from 'react';
import { Link, Navigate, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ContractsGrid } from '../components/grids/ContractsGrid';
import { AppHeader } from '../components/layout/AppHeader';
import { UserMenu } from '../components/layout/UserMenu';
import { BooksNav } from '../components/navigation/BooksNav';
import { ContractDetailSidePanel } from '../components/panels/ContractDetailSidePanel';
import { ContractTiles } from '../components/summary/ContractTiles';
import { CONTRACTS_API_URL, DEFAULT_LIST_PAGE_SIZE, MASTER_API_URL } from '../constants';
import {
  countContractStatuses,
  filterContractsByStatus,
  paginateRows,
  type ContractDetail,
  type ContractListRow,
  type ContractStatusFilter
} from '../domain/contracts';
import { hasPermission, permissionsOf } from '../domain/formatters';
import type { LoginResponse } from '../domain/types';

type AuthFetch = (url: string, init?: RequestInit) => Promise<Response>;

interface ContractsPageProps {
  auth: LoginResponse;
  onLogout: () => void | Promise<void>;
  authFetch: AuthFetch;
}

interface Paged<T> {
  items: T[];
  totalCount: number;
}

interface PartyOpt {
  id: string;
  legalName: string;
  roles: string[];
}

interface WellOpt {
  id: string;
  apiNumber: string;
  name: string;
}

interface TractOpt {
  id: string;
  name: string;
}


async function readJson<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `Request failed (${res.status})`);
  }
  return (await res.json()) as T;
}

function tabClass(active: boolean): string {
  return active ? 'app-main-nav-link app-main-nav-link--active' : 'app-main-nav-link';
}

function Shell({
  auth,
  onLogout,
  detailFull,
  children
}: {
  auth: LoginResponse;
  onLogout: () => void | Promise<void>;
  detailFull?: boolean;
  children: ReactElement;
}): ReactElement {
  return (
    <div className={`page page-authed${detailFull ? ' page-authed--detail-full' : ''}`}>
      <AppHeader
        navSlot={<BooksNav />}
        endSlot={
          <UserMenu auth={auth} onLogout={onLogout} />
        }
      />
      <div className={`app-content${detailFull ? ' app-content--detail-full' : ''}`}>{children}</div>
    </div>
  );
}

export function ContractsPage(props: ContractsPageProps): ReactElement {
  const { auth, onLogout, authFetch } = props;
  const { contractId } = useParams();
  const { pathname } = useLocation();
  const perms = permissionsOf(auth);
  const canRead = hasPermission(perms, 'contract:read', auth.role);
  const canCreate = hasPermission(perms, 'contract:create', auth.role);
  const canActivate = hasPermission(perms, 'contract:activate', auth.role);
  const canUpdate = hasPermission(perms, 'contract:update', auth.role);
  const canWriteDoi = hasPermission(perms, 'doi:update', auth.role);
  const isNew = pathname === '/contracts/new' || pathname.endsWith('/contracts/new');
  const isDetail = Boolean(contractId) && !isNew;

  if (!canRead) {
    return (
      <Shell auth={auth} onLogout={onLogout}>
        <p className="error" data-testid="contracts-denied">
          You do not have permission to view contracts.
        </p>
      </Shell>
    );
  }

  if (isNew && !canCreate) {
    return <Navigate to="/contracts" replace />;
  }

  if (isDetail && contractId) {
    return (
      <Shell auth={auth} onLogout={onLogout} detailFull>
        <ContractDetailRoute
          authFetch={authFetch}
          id={contractId}
          canActivate={canActivate}
          canUpdate={canUpdate}
          canWriteDoi={canWriteDoi}
        />
      </Shell>
    );
  }

  let body: ReactElement;
  if (isNew) {
    body = <ContractForm authFetch={authFetch} />;
  } else {
    body = (
      <ContractRegister
        authFetch={authFetch}
        canCreate={canCreate}
        canActivate={canActivate}
        canUpdate={canUpdate}
        canWriteDoi={canWriteDoi}
      />
    );
  }

  return (
    <Shell auth={auth} onLogout={onLogout}>
      <section>
        <h2>Contracts</h2>
        <nav className="app-main-nav" aria-label="Contracts register">
          <Link
            to="/contracts"
            className={tabClass(!isNew && !isDetail)}
            data-testid="contracts-tab-search"
          >
            Search
          </Link>
          {canCreate && (
            <Link to="/contracts/new" className={tabClass(isNew)} data-testid="contracts-new">
              New contract
            </Link>
          )}
        </nav>
        {body}
      </section>
    </Shell>
  );
}

function ContractRegister({
  authFetch,
  canCreate,
  canActivate,
  canUpdate,
  canWriteDoi
}: {
  authFetch: AuthFetch;
  canCreate: boolean;
  canActivate: boolean;
  canUpdate: boolean;
  canWriteDoi: boolean;
}): ReactElement {
  const navigate = useNavigate();
  const [number, setNumber] = useState('');
  const [partyName, setPartyName] = useState('');
  const [well, setWell] = useState('');
  const [allRows, setAllRows] = useState<ContractListRow[]>([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState<ContractStatusFilter>('all');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_LIST_PAGE_SIZE);
  const [detail, setDetail] = useState<ContractDetail | null>(null);
  const [detailError, setDetailError] = useState('');
  const [detailLoading, setDetailLoading] = useState(false);

  const counts = useMemo(() => countContractStatuses(allRows), [allRows]);
  const filtered = useMemo(
    () => filterContractsByStatus(allRows, statusFilter),
    [allRows, statusFilter]
  );
  const pageRows = useMemo(() => paginateRows(filtered, page, pageSize), [filtered, page, pageSize]);

  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(filtered.length / pageSize) || 1);
    if (page > maxPage) {
      setPage(maxPage);
    }
  }, [filtered.length, page, pageSize]);

  const load = useCallback(
    async (numberFilter: string, partyFilter: string, wellFilter: string) => {
      setError('');
      setLoading(true);
      const params = new URLSearchParams();
      if (numberFilter.trim()) params.set('number', numberFilter.trim());
      if (partyFilter.trim()) params.set('partyName', partyFilter.trim());
      if (wellFilter.trim()) params.set('well', wellFilter.trim());
      params.set('page', '1');
      params.set('pageSize', '100');
      try {
        const result = await readJson<Paged<ContractListRow>>(
          await authFetch(`${CONTRACTS_API_URL}?${params}`)
        );
        setAllRows(result.items);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Search failed');
        setAllRows([]);
      } finally {
        setLoading(false);
      }
    },
    [authFetch]
  );

  useEffect(() => {
    void load('', '', '');
  }, [load]);

  function onStatusFilter(filter: ContractStatusFilter): void {
    setStatusFilter(filter);
    setPage(1);
  }

  function onSearch(): void {
    setPage(1);
    void load(number, partyName, well);
  }

  useEffect(() => {
    if (!selectedId) {
      setDetail(null);
      setDetailError('');
      setDetailLoading(false);
      return;
    }
    let cancelled = false;
    setDetailLoading(true);
    setDetailError('');
    void (async () => {
      try {
        const item = await readJson<ContractDetail>(
          await authFetch(`${CONTRACTS_API_URL}/${selectedId}`)
        );
        if (!cancelled) {
          setDetail(item);
        }
      } catch (e) {
        if (!cancelled) {
          setDetail(null);
          setDetailError(e instanceof Error ? e.message : 'Load failed');
        }
      } finally {
        if (!cancelled) {
          setDetailLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authFetch, selectedId]);

  async function activate(): Promise<void> {
    if (!selectedId) {
      return;
    }
    setDetailError('');
    try {
      const item = await readJson<ContractDetail>(
        await authFetch(`${CONTRACTS_API_URL}/${selectedId}/activate`, { method: 'POST' })
      );
      setDetail(item);
      setAllRows((prev) => prev.map((row) => (row.id === item.id ? { ...row, status: item.status } : row)));
    } catch (e) {
      setDetailError(e instanceof Error ? e.message : 'Activate failed');
    }
  }

  return (
    <>
      <p>Oil and gas leases (T-01), assignments (T-06), and joint operating agreements (T-12).</p>
      <ContractTiles counts={counts} statusFilter={statusFilter} onStatusFilter={onStatusFilter} />
      {error && <p className="error">{error}</p>}
      <div className={`requests-shell${selectedId ? ' has-panel' : ''}`}>
        <ContractsGrid
          rows={pageRows}
          selectedId={selectedId}
          onSelectContract={setSelectedId}
          onOpenContractInPage={(id) => navigate(`/contracts/${id}`)}
          statusFilter={statusFilter}
          onStatusFilterChange={onStatusFilter}
          numberFilter={number}
          partyFilter={partyName}
          wellFilter={well}
          onNumberFilterChange={setNumber}
          onPartyFilterChange={setPartyName}
          onWellFilterChange={setWell}
          onSearch={onSearch}
          loading={loading}
          page={page}
          pageSize={pageSize}
          totalCount={filtered.length}
          onPageChange={setPage}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setPage(1);
          }}
          onNewContract={canCreate ? () => navigate('/contracts/new') : undefined}
        />
        {selectedId && (
          <ContractDetailSidePanel
            item={detail}
            loading={detailLoading}
            error={detailError}
            variant="panel"
            titleHint={allRows.find((row) => row.id === selectedId)?.number}
            onClose={() => setSelectedId(null)}
            onOpenFullPage={(id) => navigate(`/contracts/${id}`)}
            canActivate={canActivate}
            onActivate={activate}
            authFetch={authFetch}
            canUpdate={canUpdate}
            canWriteDoi={canWriteDoi}
            onPostAssignment={async () => {
              if (!selectedId) {
                return;
              }
              setDetailError('');
              try {
                const posted = await readJson<{ contract: ContractDetail }>(
                  await authFetch(`${CONTRACTS_API_URL}/${selectedId}/post-assignment`, { method: 'POST' })
                );
                setDetail(posted.contract);
                setAllRows((prev) =>
                  prev.map((row) => (row.id === posted.contract.id ? { ...row, status: posted.contract.status } : row))
                );
              } catch (e) {
                setDetailError(e instanceof Error ? e.message.replace(/^"|"$/g, '') : 'Post failed');
              }
            }}
            onOpenRelatedLease={(id, asOf) => navigate(`/contracts/${id}?asOf=${asOf}`)}
          />
        )}
      </div>
    </>
  );
}

function ContractDetailRoute({
  authFetch,
  id,
  canActivate,
  canUpdate,
  canWriteDoi
}: {
  authFetch: AuthFetch;
  id: string;
  canActivate: boolean;
  canUpdate: boolean;
  canWriteDoi: boolean;
}): ReactElement {
  const navigate = useNavigate();
  const asOfQuery = new URLSearchParams(useLocation().search).get('asOf') ?? undefined;
  const [item, setItem] = useState<ContractDetail | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setError('');
    setLoading(true);
    try {
      setItem(await readJson<ContractDetail>(await authFetch(`${CONTRACTS_API_URL}/${id}`)));
    } catch (e) {
      setItem(null);
      setError(e instanceof Error ? e.message : 'Load failed');
    } finally {
      setLoading(false);
    }
  }, [authFetch, id]);

  useEffect(() => {
    void load();
  }, [load]);

  async function activate(): Promise<void> {
    setError('');
    try {
      setItem(await readJson<ContractDetail>(await authFetch(`${CONTRACTS_API_URL}/${id}/activate`, { method: 'POST' })));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Activate failed');
    }
  }

  if (loading && !item && !error) {
    return <p className="hint detail-page-loading">Loading…</p>;
  }

  if (!item && error) {
    return (
      <div className="detail-page-shell">
        <p className="hint detail-page-loading">{error}</p>
        <button type="button" className="secondary" data-testid="contract-back" onClick={() => navigate('/contracts')}>
          Back to contracts
        </button>
      </div>
    );
  }

  return (
    <div className="detail-page-shell">
      <ContractDetailSidePanel
        item={item}
        error={error}
        variant="page"
        onClose={() => navigate('/contracts')}
        onBack={() => navigate('/contracts')}
        canActivate={canActivate}
        onActivate={activate}
        authFetch={authFetch}
        canUpdate={canUpdate}
        canWriteDoi={canWriteDoi}
        asOfDefault={asOfQuery}
        onPostAssignment={async () => {
          setError('');
          try {
            const posted = await readJson<{ contract: ContractDetail }>(
              await authFetch(`${CONTRACTS_API_URL}/${id}/post-assignment`, { method: 'POST' })
            );
            setItem(posted.contract);
          } catch (e) {
            setError(e instanceof Error ? e.message.replace(/^"|"$/g, '') : 'Post failed');
          }
        }}
        onOpenRelatedLease={(relatedId, asOf) => navigate(`/contracts/${relatedId}?asOf=${asOf}`)}
      />
    </div>
  );
}

function ContractForm({ authFetch }: { authFetch: AuthFetch }): ReactElement {
  const navigate = useNavigate();
  const [error, setError] = useState('');
  const [type, setType] = useState<
    'OilGasLease' | 'AssignmentOfOilAndGasInterests' | 'JointOperatingAgreement'
  >('OilGasLease');
  const [operatedByUs, setOperatedByUs] = useState(true);
  const [operatorId, setOperatorId] = useState('');
  const [copasVintage, setCopasVintage] = useState('2005');
  const [copasNote, setCopasNote] = useState('');
  const [drillingOverhead, setDrillingOverhead] = useState('5');
  const [producingOverhead, setProducingOverhead] = useState('2');
  const [overheadUnit, setOverheadUnit] = useState('%');
  const [exhibitWellId, setExhibitWellId] = useState('');
  const [title, setTitle] = useState('Demo Fee Lease — Test Tract');
  const [effectiveFrom, setEffectiveFrom] = useState(new Date().toISOString().slice(0, 10));
  const [region, setRegion] = useState('Permian');
  const [royalty, setRoyalty] = useState('0.20000000');
  const [lessorId, setLessorId] = useState('');
  const [lesseeId, setLesseeId] = useState('');
  const [tractId, setTractId] = useState('');
  const [wellId, setWellId] = useState('');
  const [parties, setParties] = useState<PartyOpt[]>([]);
  const [wells, setWells] = useState<WellOpt[]>([]);
  const [tracts, setTracts] = useState<TractOpt[]>([]);
  const [leases, setLeases] = useState<ContractListRow[]>([]);
  const [relatedLeaseId, setRelatedLeaseId] = useState('');
  const [instrumentNumber, setInstrumentNumber] = useState('');
  const [assignorId, setAssignorId] = useState('');
  const [assigneeId, setAssigneeId] = useState('');
  const [assignedWi, setAssignedWi] = useState('0.05000000');
  const [assignedNri, setAssignedNri] = useState('0.00000000');

  useEffect(() => {
    void (async () => {
      try {
        const [p, w, t, c] = await Promise.all([
          readJson<Paged<PartyOpt>>(await authFetch(`${MASTER_API_URL}/parties?pageSize=100`)),
          readJson<Paged<WellOpt>>(await authFetch(`${MASTER_API_URL}/wells?pageSize=100`)),
          readJson<Paged<TractOpt>>(await authFetch(`${MASTER_API_URL}/tracts?pageSize=100`)),
          readJson<Paged<ContractListRow>>(await authFetch(`${CONTRACTS_API_URL}?pageSize=100`))
        ]);
        setParties(p.items);
        setWells(w.items);
        setTracts(t.items);
        const leaseRows = c.items.filter((row) => !row.type || row.type === 'OilGasLease');
        setLeases(leaseRows);
        const t01 = leaseRows.find((row) => row.number === 'T-01');
        if (t01) setRelatedLeaseId(t01.id);
        const citation = p.items.find((x) => x.legalName.includes('Citation Oil'));
        const owner = p.items.find((x) => x.legalName.includes('Demo Mineral Owner'));
        const partner = p.items.find((x) => x.legalName === 'Partner Co');
        if (citation) {
          setLesseeId(citation.id);
          setAssignorId(citation.id);
          setOperatorId(citation.id);
        }
        if (owner) setLessorId(owner.id);
        if (partner) setAssigneeId(partner.id);
        const seedWell = w.items.find((x) => x.apiNumber.replace(/\D/g, '') === '42123400010000');
        if (seedWell) {
          setWellId(seedWell.id);
          setExhibitWellId(seedWell.id);
        }
        const seedTract = t.items.find((x) => x.name.includes('Demo Texas Tract'));
        if (seedTract) setTractId(seedTract.id);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Could not load master data');
      }
    })();
  }, [authFetch]);

  async function onSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    setError('');
    try {
      const body =
        type === 'JointOperatingAgreement'
          ? {
              type,
              title: title || 'Demo JOA — Test Tract',
              effectiveFrom,
              operatingRegion: region,
              citationIsOperator: operatedByUs,
              wellIds: exhibitWellId ? [exhibitWellId] : [],
              joa: {
                operatorPartyId: operatorId,
                operatedByUs,
                copasVintage,
                copasNote: copasVintage === 'Other' ? copasNote : undefined,
                relatedContractId: relatedLeaseId || undefined,
                drillingOverhead: drillingOverhead ? Number(drillingOverhead) : undefined,
                producingOverhead: producingOverhead ? Number(producingOverhead) : undefined,
                overheadUnit: overheadUnit || undefined,
                exhibitA: exhibitWellId ? [{ wellId: exhibitWellId, included: true }] : []
              }
            }
          : type === 'AssignmentOfOilAndGasInterests'
          ? {
              type,
              title: title || 'Assignment of oil and gas interests',
              effectiveFrom,
              operatingRegion: region,
              recordingNumber: instrumentNumber || undefined,
              assignment: {
                relatedContractId: relatedLeaseId,
                instrumentNumber: instrumentNumber || undefined,
                lines: [
                  {
                    assignorOwnerId: assignorId,
                    assigneeOwnerId: assigneeId,
                    interestClass: 'WorkingInterest',
                    assignedWi,
                    assignedNri
                  }
                ]
              }
            }
          : {
              type: 'OilGasLease',
              title,
              effectiveFrom,
              operatingRegion: region,
              royalty: Number(royalty),
              citationIsOperator: true,
              paidUp: false,
              parties: [
                ...(lessorId ? [{ partyId: lessorId, role: 'Lessor' }] : []),
                ...(lesseeId ? [{ partyId: lesseeId, role: 'Lessee' }] : [])
              ],
              wellIds: wellId ? [wellId] : [],
              tractIds: tractId ? [tractId] : []
            };
      const created = await readJson<ContractDetail>(
        await authFetch(CONTRACTS_API_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body)
        })
      );
      navigate(`/contracts/${created.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message.replace(/^"|"$/g, '') : 'Save failed');
    }
  }

  return (
    <section>
      <p>
        <Link to="/contracts">Back to contracts</Link>
      </p>
      <h2>
        {type === 'AssignmentOfOilAndGasInterests'
          ? 'New assignment (T-06)'
          : type === 'JointOperatingAgreement'
            ? 'New joint operating agreement (T-12)'
            : 'New oil and gas lease'}
      </h2>
      {error && <p className="error">{error}</p>}
      <form className="grid" data-testid="contract-create-form" onSubmit={(e) => void onSubmit(e)}>
        <select
          data-testid="contract-type"
          value={type}
          onChange={(e) => {
            const next = e.target.value as typeof type;
            setType(next);
            if (next === 'AssignmentOfOilAndGasInterests') {
              setTitle('Partial WI assignment — T-01');
            } else if (next === 'JointOperatingAgreement') {
              setTitle('Demo JOA — Test Tract');
            }
          }}
        >
          <option value="OilGasLease">Oil and Gas Lease</option>
          <option value="AssignmentOfOilAndGasInterests">Assignment of Oil and Gas Interests</option>
          <option value="JointOperatingAgreement">Joint Operating Agreement</option>
        </select>
        <input required value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title" />
        <input required type="date" value={effectiveFrom} onChange={(e) => setEffectiveFrom(e.target.value)} />
        <select value={region} onChange={(e) => setRegion(e.target.value)}>
          <option>Permian</option>
          <option>MidContinent</option>
          <option>Rockies</option>
          <option>IllinoisBasin</option>
        </select>
        {type === 'JointOperatingAgreement' ? (
          <>
            <select required data-testid="joa-operator" value={operatorId} onChange={(e) => setOperatorId(e.target.value)}>
              <option value="">Operator</option>
              {parties.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.legalName}
                </option>
              ))}
            </select>
            <label>
              <input
                type="checkbox"
                data-testid="joa-operated-by-us"
                checked={operatedByUs}
                onChange={(e) => setOperatedByUs(e.target.checked)}
              />{' '}
              Operated by us
            </label>
            <select data-testid="joa-copas" value={copasVintage} onChange={(e) => setCopasVintage(e.target.value)}>
              <option value="1984">COPAS 1984</option>
              <option value="2005">COPAS 2005</option>
              <option value="2012">COPAS 2012</option>
              <option value="Other">COPAS Other</option>
            </select>
            {copasVintage === 'Other' && (
              <input
                required
                value={copasNote}
                onChange={(e) => setCopasNote(e.target.value)}
                placeholder="COPAS vintage note"
              />
            )}
            <select value={relatedLeaseId} onChange={(e) => setRelatedLeaseId(e.target.value)}>
              <option value="">Related T-01 (optional)</option>
              {leases.map((row) => (
                <option key={row.id} value={row.id}>
                  {row.number} {row.title}
                </option>
              ))}
            </select>
            <select
              required
              data-testid="joa-exhibit-well"
              value={exhibitWellId}
              onChange={(e) => setExhibitWellId(e.target.value)}
            >
              <option value="">Exhibit A well</option>
              {wells.map((w) => (
                <option key={w.id} value={w.id}>
                  {w.apiNumber} {w.name}
                </option>
              ))}
            </select>
            <input
              value={drillingOverhead}
              onChange={(e) => setDrillingOverhead(e.target.value)}
              placeholder="Drilling overhead (stored only)"
            />
            <input
              value={producingOverhead}
              onChange={(e) => setProducingOverhead(e.target.value)}
              placeholder="Producing overhead (stored only)"
            />
            <input value={overheadUnit} onChange={(e) => setOverheadUnit(e.target.value)} placeholder="Overhead unit" />
            <p className="hint">Rates are stored on the header. JIB is not calculated.</p>
          </>
        ) : type === 'OilGasLease' ? (
          <>
            <input required value={royalty} onChange={(e) => setRoyalty(e.target.value)} placeholder="Royalty (0–1)" />
            <select required value={lessorId} onChange={(e) => setLessorId(e.target.value)}>
              <option value="">Lessor</option>
              {parties.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.legalName}
                </option>
              ))}
            </select>
            <select required value={lesseeId} onChange={(e) => setLesseeId(e.target.value)}>
              <option value="">Lessee</option>
              {parties.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.legalName}
                </option>
              ))}
            </select>
            <select value={tractId} onChange={(e) => setTractId(e.target.value)}>
              <option value="">Tract (optional if well set)</option>
              {tracts.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
            <select value={wellId} onChange={(e) => setWellId(e.target.value)}>
              <option value="">Well (optional if tract set)</option>
              {wells.map((w) => (
                <option key={w.id} value={w.id}>
                  {w.apiNumber} {w.name}
                </option>
              ))}
            </select>
          </>
        ) : (
          <>
            <select required data-testid="assignment-related" value={relatedLeaseId} onChange={(e) => setRelatedLeaseId(e.target.value)}>
              <option value="">Related T-01</option>
              {leases.map((row) => (
                <option key={row.id} value={row.id}>
                  {row.number} {row.title}
                </option>
              ))}
            </select>
            <input
              value={instrumentNumber}
              onChange={(e) => setInstrumentNumber(e.target.value)}
              placeholder="Instrument number"
            />
            <select required value={assignorId} onChange={(e) => setAssignorId(e.target.value)}>
              <option value="">Assignor</option>
              {parties.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.legalName}
                </option>
              ))}
            </select>
            <select required value={assigneeId} onChange={(e) => setAssigneeId(e.target.value)}>
              <option value="">Assignee</option>
              {parties.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.legalName}
                </option>
              ))}
            </select>
            <input required value={assignedWi} onChange={(e) => setAssignedWi(e.target.value)} placeholder="Assigned WI" />
            <input required value={assignedNri} onChange={(e) => setAssignedNri(e.target.value)} placeholder="Assigned NRI" />
          </>
        )}
        <button type="submit">Save draft</button>
      </form>
    </section>
  );
}
